import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { currentUserHasPermission, hasValue, getModuleType } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { navigateToNewCitation } from '@/services/navigation';
import { viewCitation } from '@/services/citation-service';

// Access at call time since MyBuiltScripts.js loads after ReactBundle.js
function getCurrentPerson() { return (window as any).currentPerson; }

interface PersonCitationsGridProps {
  personId?: number | null;
  title?: string;
  isReadOnly?: boolean;
}

export function PersonCitationsGrid({
  personId = null,
  title = 'Citations',
  isReadOnly = false,
}: PersonCitationsGridProps) {
  const { theme } = useTheme();

  const [citations, setCitations] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('dateOfIssuance');
  const [sortDir, setSortDir] = useState('desc');

  // Track sort/page in refs so the loadCitations callback always sees current values
  const sortFieldRef = useRef(sortField);
  const sortDirRef = useRef(sortDir);
  const currentPageRef = useRef(currentPage);

  sortFieldRef.current = sortField;
  sortDirRef.current = sortDir;
  currentPageRef.current = currentPage;

  // Permissions
  const canCreateCitation = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE),
    []
  );

  // Computed
  const showPagination = totalPages > 1;

  const paginationInfo = useMemo(() => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalRecords);
    return `${start}-${end} of ${totalRecords}`;
  }, [currentPage, pageSize, totalRecords]);

  // Load citations
  const loadCitations = useCallback(() => {
    if (!personId) {
      setCitations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const start = (currentPageRef.current - 1) * pageSize;

    const filters = {
      sortField: sortFieldRef.current,
      sortDir: sortDirRef.current,
    };

    try {
      api.getCitationsForPerson(
        personId,
        filters,
        start,
        pageSize,
        (result: any) => {
          const rows = result.rows || [];
          const records = result.records || 0;
          setCitations(rows);
          setTotalRecords(records);
          setTotalPages(Math.ceil(records / pageSize) || 1);

          // Update citation count in the Person
          const cp = getCurrentPerson();
          if (hasValue(cp) && cp.id === personId) {
            cp.citationCount = records;
          }

          setIsLoading(false);
        },
        (error: any) => {
          console.error('Citations API error:', error);
          App.showPopupNotification('Failed to load citations.', 'error');
          setIsLoading(false);
        }
      );
    } catch (e) {
      console.error('Citations load exception:', e);
      setIsLoading(false);
    }
  }, [personId, pageSize]);

  // Mount: check mobile, add resize listener
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Load on mount if personId exists
  useEffect(() => {
    if (hasValue(personId)) {
      loadCitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On personId change: reset to page 1, reload
  const prevPersonIdRef = useRef(personId);
  useEffect(() => {
    if (personId !== prevPersonIdRef.current) {
      prevPersonIdRef.current = personId;
      if (hasValue(personId)) {
        setCurrentPage(1);
        currentPageRef.current = 1;
        loadCitations();
      }
    }
  }, [personId, loadCitations]);

  // Reload when sort or page changes (not on initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (hasValue(personId)) {
      loadCitations();
    }
  }, [sortField, sortDir, currentPage, loadCitations, personId]);

  // Actions
  const openNewCitation = useCallback(() => {
    if (isReadOnly) {
      App.showPopupNotification('Edit mode required.', 'warning');
      return;
    }
    if (!canCreateCitation) {
      App.showPopupNotification('You do not have permission to create citations.', 'error');
      return;
    }
    if (personId) {
      navigateToNewCitation(personId);
    }
  }, [isReadOnly, canCreateCitation, personId]);

  const handleViewCitation = useCallback(
    (citationId: number) => {
      viewCitation(citationId, 'person', personId ?? undefined);
    },
    [personId]
  );

  const getCallCardLink = useCallback((citation: any): string => {
    if (citation.callCardId) {
      const moduleType = getModuleType().toUpperCase();
      const functionName = moduleType === 'CAD' ? 'CAD_CallCard' : 'MDT_ShowCallCard';
      return `/?sectionName=${moduleType}&tab=Search&function=${functionName}&multiModule=true&params=${citation.callCardId}`;
    }
    return '';
  }, []);

  const formatDate = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return (
      (month < 10 ? '0' : '') +
      month +
      '/' +
      (day < 10 ? '0' : '') +
      day +
      '/' +
      date.getFullYear()
    );
  }, []);

  const formatFine = useCallback((citation: any): string => {
    if (citation.fineToBeDetermined) return 'TBD';
    if (citation.fineAmount !== null && citation.fineAmount !== undefined) {
      return '$' + citation.fineAmount.toFixed(2);
    }
    return 'TBD';
  }, []);

  const formatViolations = useCallback((citation: any): string => {
    if (citation.violationTypeValues && citation.violationTypeValues.length > 0) {
      return citation.violationTypeValues.join(', ');
    }
    return '';
  }, []);

  const getStatusClass = useCallback((status: string): string => {
    if (status === 'issued') return 'status-issued';
    if (status === 'voided') return 'status-voided';
    return 'status-draft';
  }, []);

  const sortBy = useCallback(
    (field: string) => {
      if (sortFieldRef.current === field) {
        const newDir = sortDirRef.current === 'asc' ? 'desc' : 'asc';
        setSortDir(newDir);
        sortDirRef.current = newDir;
      } else {
        setSortField(field);
        setSortDir('asc');
        sortFieldRef.current = field;
        sortDirRef.current = 'asc';
      }
      setCurrentPage(1);
      currentPageRef.current = 1;
    },
    []
  );

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      currentPageRef.current = page;
    },
    [totalPages]
  );

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  return (
    <div className={`citations-grid person-citations-grid ${theme}`}>
      {/* Header */}
      {/* TODO: Hide "New Citation" button when offenderMode === 2 (Disabled).
          Citation settings (offenderMode) are not loaded in this component.
          Either pass offenderMode as a prop or load citation settings here. */}
      <div className="header">
        <h1>{title}</h1>
        <button
          className="btn-add"
          onClick={openNewCitation}
          disabled={isReadOnly || !canCreateCitation}
          title={
            isReadOnly
              ? 'Edit mode required'
              : !canCreateCitation
                ? 'No permission'
                : 'New Citation'
          }
        >
          <i className="fas fa-plus"></i> CITATION
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i> Loading citations...
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (citations ?? []).length === 0 && (
        <div className="empty-state">No citations found for this person.</div>
      )}

      {/* Content wrapper (both desktop and mobile rendered, CSS controls visibility) */}
      {!isLoading && (citations ?? []).length > 0 && (
        <div className="content-wrapper">
          {/* Desktop Table */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="col-citation-num" onClick={() => sortBy('citationNumber')}>
                    Citation #
                    {sortField === 'citationNumber' && (
                      <i
                        className={
                          sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
                        }
                      ></i>
                    )}
                  </th>
                  <th className="col-status">Status</th>
                  <th className="col-date" onClick={() => sortBy('dateOfIssuance')}>
                    Date
                    {sortField === 'dateOfIssuance' && (
                      <i
                        className={
                          sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
                        }
                      ></i>
                    )}
                  </th>
                  <th className="col-call-card">Call Card</th>
                  <th className="col-violations">Violation(s)</th>
                  <th className="col-fine">Fine</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {(citations ?? []).map((citation) => (
                  <tr key={'desktop-' + citation.id}>
                    <td>
                      <a
                        role="button"
                        className="citation-link"
                        onClick={() => handleViewCitation(citation.id)}
                      >
                        {citation.citationNumber || 'DRAFT'}
                      </a>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getStatusClass(citation.status)}`}
                      >
                        {(citation.status || 'draft').toUpperCase()}
                      </span>
                    </td>
                    <td>{formatDate(citation.dateOfIssuance)}</td>
                    <td>
                      {getCallCardLink(citation) ? (
                        <a href={getCallCardLink(citation)}>{citation.callCardId}</a>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    <td>{formatViolations(citation)}</td>
                    <td>{formatFine(citation)}</td>
                    <td className="actions">
                      <button
                        className="btn-view"
                        onClick={() => handleViewCitation(citation.id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {showPagination && (
              <div className="pagination">
                <span className="pagination-info">{paginationInfo}</span>
                <div className="pagination-controls">
                  <button onClick={prevPage} disabled={currentPage <= 1}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="page-indicator">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button onClick={nextPage} disabled={currentPage >= totalPages}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards">
            {(citations ?? []).map((citation) => (
              <div key={'mobile-' + citation.id} className="card">
                <div className="card-header">
                  <div className="card-header-content">
                    <a
                      role="button"
                      className="card-citation-num"
                      onClick={() => handleViewCitation(citation.id)}
                    >
                      {citation.citationNumber || 'DRAFT'}
                    </a>
                    <span
                      className={`status-badge ${getStatusClass(citation.status)}`}
                    >
                      {(citation.status || 'draft').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="card-grid">
                  <div className="card-field">
                    <div className="card-field-label">Date</div>
                    <div className="card-field-value">
                      {formatDate(citation.dateOfIssuance) || '-'}
                    </div>
                  </div>
                  <div className="card-field">
                    <div className="card-field-label">Fine</div>
                    <div className="card-field-value">{formatFine(citation)}</div>
                  </div>
                  <div className="card-field card-field-full">
                    <div className="card-field-label">Call Card</div>
                    <div className="card-field-value">
                      {getCallCardLink(citation) ? (
                        <a href={getCallCardLink(citation)}>{citation.callCardId}</a>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>
                  <div className="card-field card-field-full">
                    <div className="card-field-label">Violation(s)</div>
                    <div className="card-field-value">
                      {formatViolations(citation) || '-'}
                    </div>
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-view-mobile"
                    onClick={() => handleViewCitation(citation.id)}
                  >
                    <i className="fas fa-eye"></i> View Details
                  </button>
                </div>
              </div>
            ))}

            {/* Mobile Pagination */}
            {showPagination && (
              <div className="pagination mobile-pagination">
                <span className="pagination-info">{paginationInfo}</span>
                <div className="pagination-controls">
                  <button onClick={prevPage} disabled={currentPage <= 1}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="page-indicator">
                    {currentPage} / {totalPages}
                  </span>
                  <button onClick={nextPage} disabled={currentPage >= totalPages}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

(window as any).PersonCitationsGridReact = PersonCitationsGrid;
